import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import ErrorModal from "../../../utils/ErrorModal";
import { useNavigate, useParams } from "react-router-dom";

const gstOptions = [0, 5, 12, 18, 28];

const AddRebid = ({ rfq, data, setData, setStep, user }) => {
  const fileInputRef = useRef(null);
  const [errorModal, setErrorModal] = useState(false);
  const [latestBid, setLatestBid] = useState();
  const vendorId = JSON.parse(localStorage.getItem("vendor"));
  const navigate = useNavigate();
  const [currency, setCurrency] = useState();
  const { rfqId } = useParams();
  const [errorMsg, setErrorMsg] = useState();
  const [isImporting, setIsImporting] = useState(false);

  useEffect(() => {
    const getCurrency = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/org-currency?rfqId=${rfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        setCurrency(res.data);
        localStorage.removeItem("isRebid");
      } catch (error) {
        console.log(error);
      }
    };
    getCurrency();
  }, []);

  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  // useEffect(() => {
  //   const getWorkItems = async () => {
  //     try {
  //       const res = await axios.get(
  //         `${process.env.REACT_APP_BACKEND_URL}/tender/work-items?tenderId=${tender.tenderId}`,
  //         { headers: { Authorization: `Bearer ${user.accessToken}` } }
  //       );
  //       if (res.data.length > 0) {
  //         setData(res.data.map((item) => ({ ...item, rate: 0, total: 0 })));
  //       }
  //     } catch (error) {
  //       console.log(error);
  //     }
  //   };
  //   getWorkItems();
  // }, []);

  useEffect(() => {
    const getLatestBid = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/bid/get-latest?vendorId=${vendorId}&rfqId=${rfqId}`,
          { headers: { Authorization: `Bearer ${user.accessToken}` } },
        );
        setLatestBid(res.data);
      } catch (error) {
        console.log(error);
      }
    };
    getLatestBid();
  }, []);

  const handleChange = (index, field, value) => {
    if(field !== "gst") {
      if (value.includes(".")) {
        const [intPart, decPart] = value.split(".");
        if (decPart.length > 2) {
          value = intPart + "." + decPart.slice(0, 2);
        }
      }
    }
    const updatedData = [...data];
    updatedData[index][field] = value;
    updatedData[index].total =
      updatedData[index].quantity * updatedData[index].rate;
    setData(updatedData);
  };

  const exportToExcel = () => {
    const cleanedData = data.map((row) => ({
      code: row.code || "",
      description: row.description || "",
      section: row.section || "",
      unit: row.unit || "",
      quantity: row.quantity || 0,
      gst: row.gst || 0,
      currency:
        typeof row.currency === "string"
          ? row.currency
          : row?.currency?.currency?.shortform || "",
      rate: row.rate || 0,
      remark: row.remark || "",
      total: row.total || 0,
    }));
    const worksheet = XLSX.utils.json_to_sheet(cleanedData, {
      cellStyles: true,
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Work Items");
    XLSX.writeFile(workbook, "BidDetails.xlsx");
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setIsImporting(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      const bufferArray = e.target.result;
      const workbook = XLSX.read(bufferArray, { type: "array" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const parsedData = XLSX.utils.sheet_to_json(sheet);

      const expectedHeaders = [
        "code",
        "description",
        "unit",
        "quantity",
        "currency",
        "rate",
        "gst",
        "remark",
      ];
      const actualHeaders = Object.keys(parsedData[0] || {})?.map((h) =>
        h.toLowerCase().trim(),
      );

      const isFormatCorrect = expectedHeaders.every((header) =>
        actualHeaders.includes(header),
      );

      if (!isFormatCorrect) {
        setErrorModal(true);
        setErrorMsg("Please upload excel in correct format.");
        event.target.value = null;
        return;
      }

      const validShortforms = new Set(
        currency.map((unit) => unit.shortform?.toLowerCase()),
      );

      let hasInvalidCurrency = false;

      const normalize = (str) =>
        str?.toString().replace(/\s+/g, " ").trim().toLowerCase();

      const updatedData = data.map((row) => {
        const matchingRow = parsedData.find(
          (excelRow) =>
            excelRow.code === row.code &&
            normalize(excelRow.description) === normalize(row.description) &&
            excelRow.section === row.section &&
            excelRow.unit === row.unit &&
            excelRow.quantity === row.quantity,
        );
        let updatedRate = row.rate;
        const updatedCurrency = matchingRow?.currency ?? row.currency;
        const shortform = normalize(updatedCurrency);
        const isValidShortform = validShortforms.has(shortform);

        if (!isValidShortform) {
          hasInvalidCurrency = true;
          row.shortform = ""; // clear invalid unit
        }

        if (matchingRow) {
          if (matchingRow.rate !== undefined) {
            updatedRate = parseFloat(matchingRow.rate).toFixed(2) || 0;
          }
        }

        const qty = parseFloat(row.quantity).toFixed(2) || 0;
        const gst = parseFloat(row.gst) || 0;
        const total = qty * updatedRate;

        return {
          ...row,
          rate: updatedRate,
          gst: matchingRow?.gst || 0,
          remark: matchingRow?.remark ?? row.remark,
          currency: updatedCurrency,
          total: total,
        };
      });

      setData(updatedData);
      setIsImporting(false);
      event.target.value = null;
    };
    reader.readAsArrayBuffer(file);
  };

  const grandTotalsByCurrency = data.reduce((acc, row) => {
    const shortform =
      typeof row.currency === "string" ? row.currency : row.currency?.shortform;

    if (!shortform) return acc;

    const currencyObj = currency?.find(
      (c) => c.currency.shortform === shortform,
    );

    const symbol = currencyObj?.currency?.symbol || shortform;

    if (!acc[shortform]) {
      acc[shortform] = { symbol, total: 0 };
    }

    acc[shortform].total += parseFloat(row.total);

    return acc;
  }, {});

  const handleNext = async () => {
    const filteredData = data.filter((item) => {
      const isEmptyRow =
        (item.code?.toString().trim() === "" || item.code === undefined) &&
        (item.description?.toString().trim() === "" ||
          item.description === undefined) &&
        (item.section?.toString().trim() === "" ||
          item.section === undefined) &&
        (item.unit?.toString().trim() === "" || item.unit === undefined) &&
        (item.currency?.toString().trim() === "" ||
          item.currency === undefined) &&
        (item.gst === null || item.gst === undefined || item.gst < 0) &&
        (item.quantity === null ||
          item.quantity === undefined ||
          Number(item.quantity) <= 0) &&
        (item.rate === null ||
          item.rate === undefined ||
          Number(item.rate) <= 0);
      return !isEmptyRow;
    });

    const anyIncompleteRow = filteredData.some((item) => {
      const isFilled =
        item.code?.toString().trim() !== "" &&
        item.description?.toString().trim() !== "" &&
        item.section?.toString().trim() !== "" &&
        item.unit?.toString().trim() !== "" &&
        item.currency?.toString().trim() !== "" &&
        item.gst >= 0 &&
        item.quantity > 0 &&
        item.rate > 0;

      return !isFilled;
    });

    if (anyIncompleteRow) {
      setErrorModal(true);
      setErrorMsg("Item rates cannot be 0");
      return;
    }
    if (filteredData.length === 0) {
      setErrorModal(true);
      setErrorMsg("Item rates cannot be 0");
      return;
    }
    setData(filteredData);
    setStep((prev) => prev + 1);
  };

  const handleBack = () => {
    navigate("/vendor/dashboard");
  };

  const groupedWorkItems =
    latestBid &&
    latestBid.bidWorkItems.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});

  const currencyTotals = latestBid?.bidWorkItems.reduce((acc, item) => {
    const currencySymbol = item.currency?.currency?.symbol || "";
    acc[currencySymbol] = (acc[currencySymbol] || 0) + (item.total || 0);
    return acc;
  }, {});

  return (
    <div style={{ position: "relative" }}>
      {isImporting && (
        <div className="loading-div uploading-div">
          Uploading Sheet... <CircularProgress color="#fff" />
        </div>
      )}
      <div className="dashboard-container">
        <Typography className="form-title">Your Previous Quote</Typography>
        {latestBid && (
          <Box sx={{ display: "flex", overflow: "hidden" }}>
            {/* Fixed Left Table */}
            <table
              border={1}
              style={{
                borderCollapse: "collapse",
                width: "100%",
                textAlign: "center",
                border: "1px solid #ccc",
                marginBottom: "15px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ padding: "20px" }}>Item Code</th>
                  <th>Item Description</th>
                  <th>UOM</th>
                  <th>Quantity</th>
                  <th>GST (in %)</th>
                  <th>Currency</th>
                  <th>Rate</th>
                  <th>Remark (If any)</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(groupedWorkItems).map(
                  ([section, items], sectionIndex) => {
                    const sectionItems = latestBid.bidWorkItems.filter((bi) =>
                      items.find(
                        (wi) =>
                          wi.code === bi.code &&
                          wi.description === bi.description &&
                          wi.unit === bi.unit &&
                          wi.quantity === bi.quantity &&
                          wi.currency === bi.currency,
                      ),
                    );
                    const sectionTotal = sectionItems.reduce(
                      (sum, item) => sum + (item.total || 0),
                      0,
                    );

                    return (
                      <React.Fragment key={sectionIndex}>
                        {/* Section Header with Section Total */}
                        <tr
                          style={{
                            borderTop: "1px solid #ccc",
                            fontWeight: "bold",
                          }}
                        >
                          <td
                            colSpan={8}
                            style={{
                              textAlign: "left",
                              padding: "0px 10px",
                              height: "40px",
                            }}
                          >
                            Item Group: {section}
                          </td>
                          <td
                            style={{
                              textAlign: "end",
                              padding: "10px",
                              height: "40px",
                            }}
                          >
                            {sectionTotal}
                          </td>
                        </tr>

                        {/* Each Item Row */}
                        {items
                          ?.sort((a, b) => a.itemOrder - b.itemOrder)
                          .map((item, i) => (
                            <tr key={i}>
                              <td style={{ padding: "20px" }}>{item.code}</td>
                              <td>{item.description}</td>
                              <td>{item.unit}</td>
                              <td>{item.quantity}</td>
                              <td>{item.gst}</td>
                              <td>{item?.currency?.currency?.symbol}</td>
                              <td>{item.rate}</td>
                              <td>{item.remark}</td>
                              <td>{item.total}</td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  },
                )}

                {/* Grand Total Row */}
                {Object.entries(currencyTotals).map(([symbol, total]) => (
                  <tr
                    key={symbol}
                    style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
                  >
                    <td
                      colSpan={8}
                      style={{ textAlign: "right", padding: "20px" }}
                    >
                      Grand Total (of {symbol}):
                    </td>
                    <td style={{ width: "150px", textAlign: "center" }}>
                      {total.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        )}
      </div>
      <h2>Work Items Details</h2>
      <div className="dashboard-container">
        <table
          border={1}
          style={{
            borderCollapse: "collapse",
            minWidth: "100%",
            marginTop: "10px",
            textAlign: "center",
          }}
        >
          <thead>
            <tr>
              <th style={{ padding: "20px" }}>Item Code</th>
              <th>Description</th>
              <th>Item Group</th>
              <th>UOM</th>
              <th>Quantity</th>
              <th>GST(in %)</th>
              <th>Currency</th>
              <th style={{ width: "150px" }}>Rate</th>
              <th>Remark (if any)</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {data
              ?.sort((a, b) => a.itemOrder - b.itemOrder)
              .map((row, index) => (
                <tr key={index}>
                  <td>
                    <p>{row.code}</p>
                  </td>
                  <td>
                    <p>{row.description}</p>
                  </td>
                  <td>
                    <p>{row.section}</p>
                  </td>
                  <td>
                    <p>{row.unit}</p>
                  </td>
                  <td>
                    <p>{row.quantity}</p>
                  </td>
                  <td>
                    <p>
                      <Autocomplete
                        className={`unit-dropdown`}
                        options={gstOptions}
                        getOptionLabel={(option) => `${option}%`}
                        value={
                          gstOptions.find((opt) => opt === row.gst) ?? null
                        }
                        onChange={(event, newValue) =>
                          handleChange(index, "gst", newValue ? newValue : 0)
                        }
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        disablePortal
                        size="small"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Choose GST"
                            variant="outlined"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                minHeight: "45px",
                                fontSize: "12px",
                              },
                              "& input": {
                                padding: "15px",
                              },
                            }}
                          />
                        )}
                        filterSelectedOptions
                        autoHighlight
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                        sx={{ width: "100px" }}
                        slotProps={{
                          paper: {
                            sx: {
                              background: "#D8E9F0",
                              "& .MuiAutocomplete-option": {
                                fontSize: "12px",
                                padding: "6px 12px",
                              },
                              "& .MuiAutocomplete-groupLabel": {
                                fontWeight: "bold",
                                color: "#000",
                                fontSize: "13px",
                                backgroundColor: "#D8E9F0",
                                padding: "0px 6px",
                                lineHeight: "2",
                                position: "static",
                              },
                            },
                          },
                        }}
                      />
                    </p>
                  </td>
                  <td>
                    <Autocomplete
                      className="unit-dropdown"
                      options={currency}
                      getOptionLabel={(option) =>
                        `${option.currency.name} (${option.currency.shortform})`
                      }
                      value={
                        currency?.find(
                          (opt) => opt.currency.shortform === row?.currency,
                        ) || null
                      }
                      onChange={(event, newValue) =>
                        handleChange(
                          index,
                          "currency",
                          newValue ? newValue.currency.shortform : "",
                        )
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.currency.shortform === value.currency
                      }
                      disablePortal
                      size="small"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Choose Currency"
                          variant="outlined"
                          size="small"
                          sx={{
                            "& .MuiInputBase-root": {
                              minHeight: "45px",
                              fontSize: "12px",
                            },
                            "& input": {
                              padding: "15px",
                            },
                          }}
                        />
                      )}
                      filterSelectedOptions
                      autoHighlight
                      selectOnFocus
                      clearOnBlur={false}
                      handleHomeEndKeys
                      disableClearable
                      sx={{ width: "100%" }}
                      slotProps={{
                        paper: {
                          sx: {
                            background: "#D8E9F0",
                            "& .MuiAutocomplete-option": {
                              fontSize: "12px",
                              padding: "6px 12px",
                            },
                            "& .MuiAutocomplete-groupLabel": {
                              fontWeight: "bold",
                              color: "#000",
                              fontSize: "13px",
                              backgroundColor: "#D8E9F0",
                              padding: "0px 6px",
                              lineHeight: "2",
                              position: "static",
                            },
                          },
                        },
                      }}
                    />
                  </td>
                  <td>
                    <input
                      className="table-input"
                      type="number"
                      value={row.rate}
                      onChange={(e) =>
                        handleChange(index, "rate", e.target.value)
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="table-input"
                      type="text"
                      value={row.remark}
                      onChange={(e) =>
                        handleChange(index, "remark", e.target.value)
                      }
                      maxLength={200}
                    />
                  </td>
                  <td style={{ textAlign: "center" }}>
                    <p>
                      {(
                        parseFloat(row.rate || 0) *
                        parseFloat(row.quantity || 0)
                      ).toFixed(2)}
                    </p>
                  </td>
                </tr>
              ))}

            {Object.entries(grandTotalsByCurrency).map(
              ([shortform, { symbol, total }]) => (
                <tr
                  key={shortform}
                  style={{ backgroundColor: "#d8e9f0", fontWeight: "bold" }}
                >
                  <td
                    colSpan={9}
                    style={{ textAlign: "right", padding: "20px" }}
                  >
                    Grand Total (of {symbol}):
                  </td>
                  <td style={{ width: "150px", textAlign: "center" }}>
                    {total.toFixed(2)}
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
        <Typography variant="p" fontWeight={700} fontSize={"14px"}>
          [Important Note: <br />
          - Uploading Sheet will overwrite existing work items. <br />-
          Modifying any data other than rate column may cause errors while
          uploading. ]
        </Typography>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "end",
            gap: 50,
          }}
        >
          <Button className="btn-outlined" onClick={handleFileSelect}>
            Upload Excel
          </Button>
          <button
            onClick={exportToExcel}
            style={{
              cursor: "pointer",
              marginTop: "-50px",
              border: "none",
              background: "none",
              borderBottom: "1px solid #000",
            }}
          >
            Download Template Excel
          </button>
        </div>
      </div>
      <input
        type="file"
        accept=".xlsx, .xls"
        ref={fileInputRef}
        onChange={importFromExcel}
        style={{ display: "none" }}
      />
      <Box className="btn-container" sx={{ gap: 3 }}>
        <Button className="btn-outlined" onClick={handleBack}>
          Back
        </Button>
        <Button className="btn-contained" onClick={handleNext}>
          Next
        </Button>
      </Box>

      <ErrorModal
        open={errorModal}
        handleClose={() => setErrorModal(false)}
        msg={errorMsg}
      />
    </div>
  );
};

export default AddRebid;